import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Snackbar
} from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const ApiLogsPage = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    method: '',
    status_code: '',
    endpoint: '',
    service_filter: '', // Replaces external_service and call_type
    start_date: '',
    end_date: ''
  });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      // Convert service_filter to appropriate backend parameters
      const processedFilters = { ...filters };
      delete processedFilters.service_filter;
      
      if (filters.service_filter === 'internal') {
        processedFilters.call_type = 'internal';
      } else if (filters.service_filter === 'metakocka') {
        processedFilters.external_service = 'metakocka';
      } else if (filters.service_filter === 'elizetaplus') {
        processedFilters.external_service = 'elizetaplus';
      }
      
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(Object.entries(processedFilters).filter(([_, v]) => v !== ''))
      });

      const response = await fetch(`/api/logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      setLogs(data.logs);
      setTotalCount(data.pagination.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, token]);

  const fetchStatistics = useCallback(async () => {
    try {
      // Convert service_filter to appropriate backend parameters for statistics
      const processedFilters = { ...filters };
      delete processedFilters.service_filter;
      
      if (filters.service_filter === 'internal') {
        processedFilters.call_type = 'internal';
      } else if (filters.service_filter === 'metakocka') {
        processedFilters.external_service = 'metakocka';
      } else if (filters.service_filter === 'elizetaplus') {
        processedFilters.external_service = 'elizetaplus';
      }
      
      const queryParams = new URLSearchParams(
        Object.fromEntries(Object.entries(processedFilters).filter(([_, v]) => v !== ''))
      );

      const response = await fetch(`/api/logs/stats/summary?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.summary);
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  }, [filters, token]);

  const fetchLogDetails = async (logId) => {
    try {
      const response = await fetch(`/api/logs/${logId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch log details');
      }

      const data = await response.json();
      setSelectedLog(data.log);
      setDetailsOpen(true);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'info';
    if (statusCode >= 400 && statusCode < 500) return 'warning';
    if (statusCode >= 500) return 'error';
    return 'default';
  };

  const formatResponseTime = (ms) => {
    return ms ? `${ms}ms` : 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const clearFilters = () => {
    setFilters({
      method: '',
      status_code: '',
      endpoint: '',
      service_filter: '',
      start_date: '',
      end_date: ''
    });
  };

  const copyToClipboard = async () => {
    if (!selectedLog) return;

    // Format the API request details
    const formattedData = `
API REQUEST DETAILS
===================

REQUEST INFORMATION:
-------------------
Method: ${selectedLog.method}
URL: ${selectedLog.endpoint}
Status: ${selectedLog.status_code}
Response Time: ${formatResponseTime(selectedLog.response_time_ms)}
IP Address: ${selectedLog.ip_address}
User Agent: ${selectedLog.user_agent || 'N/A'}
Timestamp: ${formatDate(selectedLog.created_at)}
${selectedLog.external_service ? `External Service: ${selectedLog.external_service}` : 'Service: Internal API'}

REQUEST HEADERS:
---------------
${JSON.stringify(selectedLog.request_headers, null, 2)}

${selectedLog.request_body ? `REQUEST BODY:
-----------
${JSON.stringify(selectedLog.request_body, null, 2)}

` : ''}${selectedLog.response_body ? `RESPONSE BODY:
------------
${JSON.stringify(selectedLog.response_body, null, 2)}

` : ''}${selectedLog.error_message ? `ERROR MESSAGE:
------------
${selectedLog.error_message}

` : ''}Generated at: ${new Date().toLocaleString()}
    `.trim();

    try {
      await navigator.clipboard.writeText(formattedData);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = formattedData;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            API Logs
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchLogs();
              fetchStatistics();
            }}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Requests
                  </Typography>
                  <Typography variant="h5">
                    {statistics.total_requests?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Response Time
                  </Typography>
                  <Typography variant="h5">
                    {formatResponseTime(statistics.average_response_time_ms)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Success Rate
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {statistics.status_distribution?.['200s'] ? 
                      `${Math.round((statistics.status_distribution['200s'] / statistics.total_requests) * 100)}%` : 
                      'N/A'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Error Rate
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {statistics.status_distribution?.['400s'] || statistics.status_distribution?.['500s'] ? 
                      `${Math.round(((statistics.status_distribution['400s'] || 0) + (statistics.status_distribution['500s'] || 0)) / statistics.total_requests * 100)}%` : 
                      '0%'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select
                  value={filters.method}
                  label="Method"
                  onChange={(e) => handleFilterChange('method', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Code</InputLabel>
                <Select
                  value={filters.status_code}
                  label="Status Code"
                  onChange={(e) => handleFilterChange('status_code', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="200">200 OK</MenuItem>
                  <MenuItem value="201">201 Created</MenuItem>
                  <MenuItem value="400">400 Bad Request</MenuItem>
                  <MenuItem value="401">401 Unauthorized</MenuItem>
                  <MenuItem value="404">404 Not Found</MenuItem>
                  <MenuItem value="500">500 Server Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Endpoint"
                value={filters.endpoint}
                onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                placeholder="/api/orders"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Service</InputLabel>
                <Select
                  value={filters.service_filter}
                  label="Service"
                  onChange={(e) => handleFilterChange('service_filter', e.target.value)}
                >
                  <MenuItem value="">All Services</MenuItem>
                  <MenuItem value="internal">Internal API</MenuItem>
                  <MenuItem value="metakocka">MetaKocka</MenuItem>
                  <MenuItem value="elizetaplus">Eli Z</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="datetime-local"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="datetime-local"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearFilters}
                size="small"
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Logs Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Response Time</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                                      <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow hover>
                        <TableCell>{formatDate(log.created_at)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.method} 
                            size="small" 
                            color={log.method === 'GET' ? 'info' : log.method === 'POST' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.endpoint}
                        </TableCell>
                        <TableCell>
                          {log.external_service ? (
                            <Chip 
                              label={log.external_service === 'metakocka' ? 'MetaKocka' : 'Eli Z'} 
                              size="small" 
                              color="secondary"
                            />
                          ) : (
                            <Chip 
                              label="Internal" 
                              size="small" 
                              color="default"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={log.status_code} 
                            size="small" 
                            color={getStatusColor(log.status_code)}
                          />
                        </TableCell>
                        <TableCell>{formatResponseTime(log.response_time_ms)}</TableCell>
                        <TableCell>{log.ip_address}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                          >
                            {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => fetchLogDetails(log.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              <Typography variant="h6" gutterBottom component="div">
                                Quick Details
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2">
                                    <strong>User Agent:</strong> {log.user_agent || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2">
                                    <strong>User ID:</strong> {log.user_id || 'Anonymous'}
                                  </Typography>
                                </Grid>
                                {log.error_message && (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="error">
                                      <strong>Error:</strong> {log.error_message}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Log Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Log Details</DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Request Info</Typography>
                  <Typography><strong>Method:</strong> {selectedLog.method}</Typography>
                  <Typography><strong>Endpoint:</strong> {selectedLog.endpoint}</Typography>
                  <Typography><strong>Status:</strong> {selectedLog.status_code}</Typography>
                  <Typography><strong>Response Time:</strong> {formatResponseTime(selectedLog.response_time_ms)}</Typography>
                  <Typography><strong>IP:</strong> {selectedLog.ip_address}</Typography>
                  <Typography><strong>User Agent:</strong> {selectedLog.user_agent || 'N/A'}</Typography>
                  <Typography><strong>Timestamp:</strong> {formatDate(selectedLog.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Headers</Typography>
                  <Paper sx={{ p: 1, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                      {JSON.stringify(selectedLog.request_headers, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
                {selectedLog.request_body && (
                  <Grid item xs={12}>
                    <Typography variant="h6">Request Body</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                        {JSON.stringify(selectedLog.request_body, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
                {selectedLog.response_body && (
                  <Grid item xs={12}>
                    <Typography variant="h6">Response Body</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                        {JSON.stringify(selectedLog.response_body, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
                {selectedLog.error_message && (
                  <Grid item xs={12}>
                    <Typography variant="h6" color="error">Error Message</Typography>
                    <Typography color="error">{selectedLog.error_message}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              startIcon={<ContentCopyIcon />}
              onClick={copyToClipboard}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Copy to Clipboard
            </Button>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Copy Success Snackbar */}
        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          message="API request details copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </Container>
  );
};

export default ApiLogsPage;
